<!--
  App.vue
  
  2025-05-24: Added WhatsApp floating action button component to main app layout
-->
<template>
  <v-app :class="{ 'search-widget': isSearchWidget }">
    <v-main class="pa-0">
      <router-view />
      <GlobalSnack />
      <whatsapp-floating-button />
      <v-btn
        v-if="settings.show_cookie_consent && $route.path === '/'"
        text
        small
        color="secondary"
        id="open_preferences_center"
        class="text-none mr-2 cookie-prefs-btn mt-8"
      >
        {{ $t("update_cookies_preferences") }}
      </v-btn>
    </v-main>
    <exit-intent-popup
      v-model="showExitIntentPopup"
      :primary-color="settings?.primary_color"
      :settings="settings"
    />
  </v-app>
</template>

<script>
import GlobalSnack from "@/components/common/global-snack.vue";
import { mapState, mapActions } from "vuex";
import ExitIntentPopup from "@/components/common/ExitIntentPopup.vue";
import WhatsappFloatingButton from "@/components/common/whatsapp-floating-button.vue";
import DeviceMixin from "@/mixins/device-mixin";
import { isUserInIsrael } from "@/utils/israelCheck";
import { loadAccessibilityWidget } from "@/utils/loadTabNav";

export default {
  name: "App",
  components: { GlobalSnack, ExitIntentPopup, WhatsappFloatingButton },
  mixins: [DeviceMixin],
  data: () => ({
    showExitIntentPopup: false,
    sessionKey: "exitIntentShown",
    exitIntentTimerId: null,
    popupDelay: 750,
    urlParam: "exit-intent=true",
  }),
  computed: {
    ...mapState({
      settings: (state) => state.settingsModule.settings,
      listing: (state) => state.listingsModule.currentListing,
    }),
    isSearchWidget() {
      return this.$route.path.includes("/search-widget");
    },
  },
  mounted() {
    this.initSession();

    // Check if already shown this session (do this first)
    if (sessionStorage.getItem(this.sessionKey)) {
      return;
    }

    // Check if triggered by URL param on load (before mobile check)
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has("exit-intent")) {
      // Trigger immediately if param exists (works on mobile too in this case)
      this.triggerPopup();
      // Important: Don't add mouse listeners if triggered by URL
      return;
    }

    // If not triggered by URL, THEN check for mobile to disable mouse listeners
    if (this.isMobile) {
      return;
    }

    // Add listeners ONLY for non-mobile and if not triggered by URL
    document.addEventListener("mouseout", this.handleMouseOut);
    document.addEventListener("mouseenter", this.handleMouseEnter);
  },
  beforeDestroy() {
    document.removeEventListener("mouseout", this.handleMouseOut);
    document.removeEventListener("mouseenter", this.handleMouseEnter);
    if (this.exitIntentTimerId) {
      clearTimeout(this.exitIntentTimerId);
    }
  },
  watch: {
    listing(val) {
      this.setMetaTags(val);
    },
    settings: {
      handler() {
        this.initializeCookieConsent();
        if (!this.$route.path.includes("/listing/")) {
          this.setMetaTags({
            nickname: this.settings.sections.header.title,
            title: this.settings.sections.header.sub_title,
            picture: this.settings.bg_image,
          });
        }

        // Initialize Google Tag Manager
        const gtmId = this.settings.analytics?.gtm;
        if (gtmId) {
          // Add GTM script to head
          const gtmScript = document.createElement("script");
          gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,"script","dataLayer","${gtmId}");`;
          document.head.insertBefore(gtmScript, document.head.firstChild);

          // Add GTM noscript iframe to body
          const noscript = document.createElement("noscript");
          const iframe = document.createElement("iframe");
          iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
          iframe.height = "0";
          iframe.width = "0";
          iframe.style.display = "none";
          iframe.style.visibility = "hidden";
          noscript.appendChild(iframe);
          document.body.insertBefore(noscript, document.body.firstChild);
        }

        // Initialize Google Analytics
        const tag = this.settings.analytics.google;
        if (tag) {
          const tagManagerScript = document.createElement("script");
          const url = `https://www.googletagmanager.com/gtag/js?id=${tag}`;
          tagManagerScript.setAttribute("src", url);
          document.head.appendChild(tagManagerScript);

          window.dataLayer = window.dataLayer || [];
          window.gtag = function () {
            window.dataLayer.push(arguments);
          };
          window.gtag("js", new Date());
          window.gtag("config", tag);
        }

        // Initialize Facebook Pixel
        const pixelId = this.settings.analytics.pixel;
        if (pixelId) {
          this.initializeFacebookPixel(pixelId);
        }

        // Inject custom scripts
        const script = document.createElement("script");
        script.innerHTML = this.settings.injected_scripts;
        document.head.appendChild(script);

        // Load accessibility widget for Israel users
        if (isUserInIsrael()) loadAccessibilityWidget();
      },
    },
    showExitIntentPopup(newValue, oldValue) {
      if (oldValue === true && newValue === false) {
        this.removeUrlParam();
      }
    },
  },
  methods: {
    ...mapActions("analyticsModule", ["initSession"]),
    setMetaTags({ title, picture, marketing_content }) {
      const image = picture?.replace(
        "upload/",
        "upload/w_600,h_336,c_fill,q_auto,f_auto/"
      );
      const summary = marketing_content?.summary;
      const description = summary ? summary.slice(0, 147) + "..." : "";
      this.updateMetaTag("property", "og:title", title);
      this.updateMetaTag("property", "og:description", description);
      this.updateMetaTag("property", "og:image", image);
      this.updateMetaTag("property", "og:image:alt", "property image");
      this.updateMetaTag("property", "og:url", window.location.href);
      this.updateMetaTag("property", "og:type", "website");

      // Add Facebook Pixel meta tag if configured
      const pixelId = this.settings?.analytics?.pixel;
      if (pixelId) {
        this.updateMetaTag("facebook-domain-verification", "", pixelId);
      }
    },
    updateMetaTag(attrName, attrValue, content) {
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    },
    initializeFacebookPixel(pixelId) {
      /* eslint-disable */
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js"
      );
      window.fbq("init", pixelId);
      window.fbq("track", "PageView");
      /* eslint-enable */
    },
    initializeCookieConsent() {
      if (!this.settings.show_cookie_consent) return;

      // Add cookie consent script
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src =
        "https://www.termsfeed.com/public/cookie-consent/4.2.0/cookie-consent.js";
      script.charset = "UTF-8";
      script.onload = () => {
        /* eslint-disable no-undef */
        cookieconsent.run({
          notice_banner_type: "simple",
          consent_type: "express",
          palette: "light",
          language: "en",
          page_load_consent_levels: ["strictly-necessary"],
          notice_banner_reject_button_hide: true,
          preferences_center_close_button_hide: false,
          page_refresh_confirmation_buttons: false,
          website_name: "Boom",
          website_privacy_policy_url: "https://www.boomnow.com/privacy-policy",
        });
        /* eslint-enable no-undef */
      };

      document.body.insertBefore(script, document.body.firstChild);

      // Add noscript element
      const noscript = document.createElement("noscript");
      noscript.innerHTML =
        'Free cookie consent management tool by <a href="https://www.termsfeed.com/" rel="nofollow">TermsFeed</a>';
      document.body.insertBefore(
        noscript,
        document.body.firstChild.nextSibling
      );
    },
    handleMouseOut(event) {
      if (event.clientY <= 0 && !this.exitIntentTimerId) {
        this.exitIntentTimerId = setTimeout(() => {
          this.triggerPopup();
        }, this.popupDelay);
      }
    },
    handleMouseEnter() {
      if (this.exitIntentTimerId) {
        clearTimeout(this.exitIntentTimerId);
        this.exitIntentTimerId = null;
      }
    },
    triggerPopup() {
      if (sessionStorage.getItem(this.sessionKey)) {
        this.exitIntentTimerId = null;
        return;
      }
      this.addUrlParam();
      this.showExitIntentPopup = true;
      sessionStorage.setItem(this.sessionKey, "true");
      this.exitIntentTimerId = null;
      document.removeEventListener("mouseout", this.handleMouseOut);
      document.removeEventListener("mouseenter", this.handleMouseEnter);
    },
    addUrlParam() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has("exit-intent")) {
        url.searchParams.set("exit-intent", "true");
        history.pushState({}, "", url.toString());
      }
    },
    removeUrlParam() {
      const url = new URL(window.location.href);
      if (url.searchParams.has("exit-intent")) {
        url.searchParams.delete("exit-intent");
        history.replaceState({}, "", url.toString());
      }
    },
  },
};
</script>
<style lang="sass">
@import '~vuetify/src/styles/styles.sass'

@each $position in relative, absolute, fixed
  .p-#{$position}
    position: $position

.font-weight-semibold
  font-weight: 600 !important

.theme--light.v-divider
  border-color: map-get($grey, "lighten-3") !important

// RTL Support - Mirror margins for Hebrew
[dir="rtl"]
  // Vuetify margin classes
  .ml-1
    margin-left: 0 !important
    margin-right: 4px !important

  .ml-2
    margin-left: 0 !important
    margin-right: 8px !important

  .ml-3
    margin-left: 0 !important
    margin-right: 12px !important

  .ml-4
    margin-left: 0 !important
    margin-right: 16px !important

  .ml-auto
    margin-left: 0 !important
    margin-right: auto !important

  .ml-n2
    margin-left: 0 !important
    margin-right: -8px !important

  .mr-1
    margin-right: 0 !important
    margin-left: 4px !important

  .mr-2
    margin-right: 0 !important
    margin-left: 8px !important

  .mr-3
    margin-right: 0 !important
    margin-left: 12px !important

  .mr-4
    margin-right: 0 !important
    margin-left: 16px !important

  // Responsive margin classes
  .ml-sm-4
    @media (min-width: 600px)
      margin-left: 0 !important
      margin-right: 16px !important

  .mr-md-0
    @media (min-width: 960px)
      margin-right: 0 !important
      margin-left: 0 !important

  .mr-sm-6
    @media (min-width: 600px)
      margin-right: 0 !important
      margin-left: 24px !important

  // Custom margin-left auto cases
  .listing-info-card-item
    margin-left: 0 !important
    margin-right: auto !important

  // Handle specific component margins
  .home-filters-item
    margin-right: 0 !important
    margin-left: 16px !important

    @media (min-width: 960px)
      margin-left: 0 !important

  // Cookie preferences button
  .cookie-prefs-btn
    margin-right: 0 !important
    margin-left: 8px !important

  // Component-specific CSS margin-left overrides
  .close-btn
    margin-left: 0 !important
    margin-right: 8px !important

  // Home top destinations and reviews - handle margin-left: auto
  .home-top-destinations .v-btn,
  .home-reviews .v-btn
    margin-left: 0 !important
    margin-right: auto !important

  // Internal checkout - handle margin-left: auto
  .internal-checkout .v-btn
    margin-left: 0 !important
    margin-right: auto !important

  // Site listings - handle margin-left: auto
  .site-listings .v-btn
    margin-left: 0 !important
    margin-right: auto !important

  // Language currency picker close button
  .lang-currency-dialog .close-btn
    margin-left: 0 !important
    margin-right: 8px !important

  // Listing filters dialog
  .listing-filters-dialog .v-btn
    margin-left: 0 !important

  // Site carousel indicators
  .site-carousel .carousel-indicator
    margin-left: 0 !important
    margin-right: 4px !important

  // Text alignment adjustments
  .text-left
    text-align: right !important

  .text-right
    text-align: left !important

  // Tab alignment for language picker
  .lang-currency-tabs .v-tab
    text-align: right !important
    padding: 0 0 0 24px !important

  .tab-label
    text-align: right !important

  // Position adjustments for absolute positioned elements
  .close-btn-absolute
    right: auto !important
    left: 12px !important

  // Navigation icon positioning
  .fixed-nav-icon
    margin-left: 8px !important
    margin-right: 0 !important

  // Header button icon positioning
  .site-header .v-btn .v-icon--left
    margin-left: 8px !important
    margin-right: 0 !important

  .site-header .v-btn .v-icon--right
    margin-left: 0 !important
    margin-right: 8px !important

  // Header language/currency button
  .site-header .v-btn span
    &.mr-2
      margin-right: 0 !important
      margin-left: 8px !important

  // Gallery component RTL support
  .site-gallery-count
    right: auto !important
    left: 20px !important

  // Gallery tiles layout adjustments
  .site-gallery-tiles
    .pl-3
      padding-left: 0 !important
      padding-right: 12px !important

  // Carousel and image gallery positioning
  .v-carousel__controls
    .v-btn--icon
      margin-left: 0 !important
      margin-right: 4px !important

  // Fix flex ordering for gallery items
  .site-gallery .v-row > div:first-child
    order: 2

  .site-gallery .v-row > div:last-child
    order: 1

  // Site listings item adjustments
  .listings-item
    .ml-1
      margin-left: 0 !important
      margin-right: 4px !important

  // Form inputs and controls RTL support
  .v-input
    .v-input__control
      .v-input__slot
        direction: rtl

  .v-text-field
    .v-input__control
      .v-input__slot
        direction: rtl

    input
      text-align: right !important
      direction: rtl

  .v-select
    .v-input__control
      .v-input__slot
        direction: rtl

    .v-select__selection
      text-align: right !important

    .v-input__icon--append
      order: -1
      margin-left: 0 !important
      margin-right: 16px !important

  .v-autocomplete
    .v-input__control
      .v-input__slot
        direction: rtl

    input
      text-align: right !important
      direction: rtl

  // Enhanced form field RTL support
  .v-input__control
    flex-direction: row-reverse !important

  .v-input__prepend-outer
    order: 3 !important
    margin-left: 16px !important
    margin-right: 0 !important

  .v-input__append-outer
    order: 1 !important
    margin-right: 16px !important
    margin-left: 0 !important

  .v-input__slot
    direction: rtl !important

  .v-text-field__slot
    direction: rtl !important

  .v-select__slot
    direction: rtl !important

  // Specific icon positioning for RTL
  .v-input__icon
    &.v-input__icon--append
      order: -1 !important
      margin-left: 0 !important
      margin-right: 8px !important

    &.v-input__icon--prepend
      order: 1 !important
      margin-right: 0 !important
      margin-left: 8px !important

  // Date picker specific RTL fixes
  .v-menu__content .v-picker
    direction: rtl

  .v-picker__title
    direction: rtl
    text-align: right !important

  .v-picker__body
    direction: rtl

  .v-date-picker-header
    direction: rtl

  .v-btn--icon
    direction: ltr // Keep icon buttons in LTR for consistency

  // Input label positioning
  .v-label
    transform-origin: right center !important
    right: 0 !important
    left: auto !important

  .v-input__slot .v-label
    right: 0 !important
    left: auto !important

  // Dropdown arrow specific fixes
  .v-select__selections
    direction: rtl !important
    text-align: right !important

  .v-select__selection--comma
    direction: rtl !important

  // Form field borders and focus states
  .v-text-field > .v-input__control > .v-input__slot:before
    right: 0 !important
    left: auto !important

  .v-text-field > .v-input__control > .v-input__slot:after
    right: 0 !important
    left: auto !important

  // Padding adjustments for RTL
  .pe-sm-10
    @media (min-width: 600px)
      padding-right: 0 !important
      padding-left: 40px !important

  .pl-3
    padding-left: 0 !important
    padding-right: 12px !important

  .pr-3
    padding-right: 0 !important
    padding-left: 12px !important

  // Listing view specific adjustments
  .listing-view .px-3
    padding-left: 12px !important
    padding-right: 12px !important

  // Bottom margin class
  .bottom-margin
    @media (min-width: 960px)
      margin-left: 0 !important
      margin-right: 0 !important

  // Gallery layout improvements for RTL
  .site-gallery-tiles .v-row
    flex-direction: row-reverse !important

  // Ensure proper spacing in gallery tiles
  .site-gallery .v-col
    padding-left: 0 !important
    padding-right: 12px !important

    &:first-child
      padding-right: 0 !important

  // Carousel controls positioning
  .v-carousel__controls__item
    margin-left: 0 !important
    margin-right: 8px !important

  // Dialog positioning adjustments
  .v-dialog
    .v-carousel__controls
      direction: ltr // Keep carousel controls in LTR for consistency

  // Mobile gallery adjustments
  @media (max-width: 599px)
    .site-gallery-count
      left: 20px !important
      right: auto !important
</style>

<style lang="scss" scoped>
.search-widget {
  background-color: transparent !important;
}
</style>
